import { usePageTitle } from '../hooks/usePageTitle';
import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import { useTheme } from '../contexts/ThemeContext';

const TR = (s: string) =>
  new Date(s).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

interface Talep {
  id: string;
  kullanici_id?: string;
  email: string;
  tarih: string;
  ad?: string;
  soyad?: string;
  telefon?: string;
  mevcutEmail?: string;
  mevcutTelefon?: string;
  not?: string;
  sube_id?: string;
}

interface Ogrenci {
  id: string;
  ad: string;
  soyad: string;
  email: string;
  tel?: string;
}

interface Stat {
  sube_adi: string;
  sube_id: string | null;
  tamamlanan: number;
  bekleyen: number;
  toplam: number;
}

interface GecmisKayit {
  id: string;
  email: string;
  ad: string | null;
  soyad: string | null;
  tarih: string;
  sube_id: string | null;
  sube_adi: string | null;
}

export default function BirlestirmeTalepleri() {
  usePageTitle('Hesap Birleştirme Talepleri');
  const { isDark } = useTheme();
  const card      = isDark ? '#1a1a1a' : 'white';
  const cardAlt   = isDark ? '#1e1e1e' : '#f8f8f8';
  const border    = isDark ? '#2a2a2a' : '#eee';
  const borderSub = isDark ? '#202020' : '#f5f5f5';
  const textSec   = isDark ? '#999'    : '#888';
  const textMuted = isDark ? '#666'    : '#aaa';
  const inputBg   = isDark ? '#111'    : 'white';
  const inputBorder = isDark ? '#333'  : '#ddd';

  const [sekme, setSekme] = useState<'bekleyen' | 'gecmis'>('bekleyen');
  const [talepler, setTalepler] = useState<Talep[]>([]);
  const [gecmis, setGecmis]     = useState<GecmisKayit[]>([]);
  const [stats, setStats]       = useState<Stat[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);

  // Eşleştirme modal state
  const [secilenTalep, setSecilenTalep] = useState<Talep | null>(null);
  const [arama, setArama] = useState('');
  const [ogrenciler, setOgrenciler] = useState<Ogrenci[]>([]);
  const [aramaYukleniyor, setAramaYukleniyor] = useState(false);
  const [secilenOgrenci, setSecilenOgrenci] = useState<Ogrenci | null>(null);
  const [islemYapiliyor, setIslemYapiliyor] = useState(false);
  const [hata, setHata] = useState('');

  const yukle = () => {
    setYukleniyor(true);
    Promise.all([
      api.get('/api/auth/merge-requests'),
      api.get('/api/auth/merge-history'),
      api.get('/api/auth/merge-stats'),
    ]).then(([r1, r2, r3]) => {
      setTalepler(r1.data);
      setGecmis(r2.data);
      setStats(r3.data);
    }).catch(() => {}).finally(() => setYukleniyor(false));
  };

  useEffect(() => { yukle(); }, []);

  const ogrenciAra = useCallback((q: string) => {
    if (!q.trim()) { setOgrenciler([]); return; }
    setAramaYukleniyor(true);
    api.get(`/api/ogrenciler?arama=${encodeURIComponent(q)}&tum=true`)
      .then(r => setOgrenciler(r.data))
      .catch(() => {})
      .finally(() => setAramaYukleniyor(false));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => ogrenciAra(arama), 300);
    return () => clearTimeout(t);
  }, [arama, ogrenciAra]);

  const modalKapat = () => {
    setSecilenTalep(null);
    setArama('');
    setOgrenciler([]);
    setSecilenOgrenci(null);
    setHata('');
  };

  const ayniHesap = !!(secilenTalep && secilenOgrenci && secilenTalep.kullanici_id && secilenOgrenci.id === secilenTalep.kullanici_id);

  const talepKapat = async (talep: Talep) => {
    if (!window.confirm('Bu talep kapatılacak (birleştirme yapılmayacak). Emin misiniz?')) return;
    try {
      await api.post('/api/auth/merge-dismiss', { log_id: talep.id });
      yukle();
    } catch (err: any) {
      alert(err.response?.data?.hata || 'Kapatılamadı');
    }
  };

  const birlestir = async () => {
    if (!secilenTalep || !secilenOgrenci) return;

    if (ayniHesap) {
      if (!window.confirm('Aynı hesap seçildi — birleştirme yapılmadan talep kapatılacak. Emin misiniz?')) return;
      setIslemYapiliyor(true);
      try {
        await api.post('/api/auth/merge-dismiss', { log_id: secilenTalep.id });
        modalKapat();
        yukle();
      } catch (err: any) {
        setHata(err.response?.data?.hata || 'Kapatılamadı');
      } finally {
        setIslemYapiliyor(false);
      }
      return;
    }

    if (!window.confirm(`"${[secilenTalep.ad, secilenTalep.soyad].filter(Boolean).join(' ')}" adlı kişinin talebi "${secilenOgrenci.ad} ${secilenOgrenci.soyad}" hesabıyla birleştirilecek. Emin misiniz?`)) return;

    setIslemYapiliyor(true);
    setHata('');
    try {
      await api.post(`/api/ogrenciler/${secilenOgrenci.id}/birlestir-talep`, {
        yeni_email: secilenTalep.email,
        log_id: secilenTalep.id,
      });
      alert(`Birleştirme tamamlandı. ${secilenOgrenci.ad} ${secilenOgrenci.soyad} hesabı güncellendi.`);
      modalKapat();
      yukle();
    } catch (err: any) {
      setHata(err.response?.data?.hata || 'Birleştirme başarısız');
    } finally {
      setIslemYapiliyor(false);
    }
  };

  const toplamTamamlanan = stats.reduce((s, r) => s + Number(r.tamamlanan), 0);
  const toplamBekleyen   = stats.reduce((s, r) => s + Number(r.bekleyen), 0);

  return (
    <div style={{ padding: '24px 24px 48px' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Hesap Birleştirme</h1>
        <div style={{ fontSize: 13, color: textMuted, marginTop: 4 }}>
          Mobil uygulamadan gelen "zaten kayıtlıyım" talepleri
        </div>
      </div>

      {/* İstatistik kartları */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 20 }}>
        <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 11, color: textMuted, marginBottom: 4 }}>TAMAMLANAN</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#1D9E75' }}>{toplamTamamlanan}</div>
        </div>
        <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 11, color: textMuted, marginBottom: 4 }}>BEKLEYEN</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#e24b4a' }}>{toplamBekleyen}</div>
        </div>
        {stats.filter(s => s.sube_id).map(s => (
          <div key={s.sube_id} style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 11, color: textMuted, marginBottom: 4 }}>{s.sube_adi?.toUpperCase()}</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: '#534AB7' }}>{s.tamamlanan}</span>
              <span style={{ fontSize: 12, color: textSec }}>tamamlandı</span>
            </div>
            {Number(s.bekleyen) > 0 && (
              <div style={{ fontSize: 11, color: '#e24b4a', marginTop: 2 }}>{s.bekleyen} bekliyor</div>
            )}
          </div>
        ))}
      </div>

      {/* Sekme */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: `1px solid ${border}` }}>
        {(['bekleyen', 'gecmis'] as const).map(s => (
          <button key={s} onClick={() => setSekme(s)}
            style={{
              border: 'none', borderBottom: sekme === s ? '2px solid #534AB7' : '2px solid transparent',
              background: 'transparent', padding: '10px 18px', fontSize: 13, fontWeight: sekme === s ? 600 : 400,
              color: sekme === s ? '#534AB7' : textSec, cursor: 'pointer', marginBottom: -1,
            }}>
            {s === 'bekleyen' ? `Bekleyen Talepler${talepler.length > 0 ? ` (${talepler.length})` : ''}` : `Birleştirme Geçmişi${gecmis.length > 0 ? ` (${gecmis.length})` : ''}`}
          </button>
        ))}
      </div>

      {/* Bekleyen Talepler */}
      {sekme === 'bekleyen' && (
        <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, overflow: 'hidden' }}>
          {yukleniyor ? (
            <div style={{ padding: 32, textAlign: 'center', color: textMuted }}>Yükleniyor...</div>
          ) : talepler.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: textMuted }}>
              Bekleyen birleştirme talebi yok.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: cardAlt, borderBottom: `1px solid ${border}` }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: textSec, fontSize: 11 }}>TARİH</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: textSec, fontSize: 11 }}>TALEP EDEN</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: textSec, fontSize: 11 }}>SİSTEMDEKİ BİLGİ</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: textSec, fontSize: 11 }}>ŞUBE</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: textSec, fontSize: 11 }}>NOT</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: textSec, fontSize: 11 }}>İŞLEM</th>
                </tr>
              </thead>
              <tbody>
                {talepler.map((t, i) => (
                  <tr key={t.id ?? i} style={{ borderTop: `1px solid ${borderSub}` }}>
                    <td style={{ padding: '12px 16px', color: textMuted, whiteSpace: 'nowrap' }}>{TR(t.tarih)}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: 500 }}>{[t.ad, t.soyad].filter(Boolean).join(' ') || '—'}</div>
                      {t.telefon && <div style={{ fontSize: 12, color: textSec }}>📱 +90{t.telefon}</div>}
                      <div style={{ fontSize: 11, color: textMuted }}>{t.email}</div>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#534AB7', fontWeight: 500 }}>
                      {t.mevcutEmail ? <div>{t.mevcutEmail}</div> : null}
                      {t.mevcutTelefon ? <div style={{ fontSize: 12 }}>{t.mevcutTelefon}</div> : null}
                      {!t.mevcutEmail && !t.mevcutTelefon ? <span style={{ color: textMuted }}>—</span> : null}
                    </td>
                    <td style={{ padding: '12px 16px', color: isDark ? '#999' : '#666' }}>
                      {t.sube_id ? (
                        <span style={{ background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', color: '#534AB7', borderRadius: 4, padding: '2px 8px', fontSize: 12 }}>
                          {t.sube_id}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '12px 16px', color: isDark ? '#999' : '#666', maxWidth: 180, wordBreak: 'break-word' as const }}>
                      {t.not || '—'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => { setSecilenTalep(t); setSecilenOgrenci(null); setArama(''); setOgrenciler([]); setHata(''); }}
                          style={{ background: '#534AB7', color: 'white', border: 'none', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}
                        >
                          ⇌ Eşleştir
                        </button>
                        <button
                          onClick={() => talepKapat(t)}
                          style={{ background: 'transparent', color: textSec, border: `1px solid ${border}`, padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap' }}
                        >
                          ✕ Kapat
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Geçmiş */}
      {sekme === 'gecmis' && (
        <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, overflow: 'hidden' }}>
          {yukleniyor ? (
            <div style={{ padding: 32, textAlign: 'center', color: textMuted }}>Yükleniyor...</div>
          ) : gecmis.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: textMuted }}>
              Henüz tamamlanan birleştirme yok.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: cardAlt, borderBottom: `1px solid ${border}` }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: textSec, fontSize: 11 }}>TARİH</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: textSec, fontSize: 11 }}>KİŞİ</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: textSec, fontSize: 11 }}>E-POSTA</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: textSec, fontSize: 11 }}>ŞUBE</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: textSec, fontSize: 11 }}>DURUM</th>
                </tr>
              </thead>
              <tbody>
                {gecmis.map((g, i) => (
                  <tr key={g.id ?? i} style={{ borderTop: `1px solid ${borderSub}` }}>
                    <td style={{ padding: '12px 16px', color: textMuted, whiteSpace: 'nowrap' }}>{TR(g.tarih)}</td>
                    <td style={{ padding: '12px 16px', fontWeight: 500 }}>
                      {[g.ad, g.soyad].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td style={{ padding: '12px 16px', color: textSec, fontSize: 12 }}>{g.email}</td>
                    <td style={{ padding: '12px 16px' }}>
                      {g.sube_adi ? (
                        <span style={{ background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', color: '#534AB7', borderRadius: 4, padding: '2px 8px', fontSize: 12 }}>
                          {g.sube_adi}
                        </span>
                      ) : <span style={{ color: textMuted }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ background: isDark ? 'rgba(29,158,117,0.15)' : '#E1F5EE', color: '#085041', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                        Tamamlandı
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Eşleştirme Modalı */}
      {secilenTalep && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={e => { if (e.target === e.currentTarget) modalKapat(); }}>
          <div style={{ background: card, borderRadius: 14, padding: 28, width: 520, maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto' }}>

            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Hesap Eşleştir</div>

            {/* Talep bilgisi */}
            <div style={{ background: isDark ? '#111' : '#f8f8ff', border: `1px solid ${isDark ? '#2a2a3a' : '#e0deff'}`, borderRadius: 10, padding: 14, marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: '#534AB7', fontWeight: 600, marginBottom: 8 }}>TALEP EDEN</div>
              <div style={{ fontWeight: 600 }}>{[secilenTalep.ad, secilenTalep.soyad].filter(Boolean).join(' ') || '—'}</div>
              {secilenTalep.telefon && <div style={{ fontSize: 12, color: textSec }}>📱 +90{secilenTalep.telefon}</div>}
              <div style={{ fontSize: 12, color: textMuted }}>{secilenTalep.email}</div>
              {(secilenTalep.mevcutEmail || secilenTalep.mevcutTelefon) && (
                <div style={{ marginTop: 8, fontSize: 12, color: textSec }}>
                  Sistemdeki bilgi: <strong style={{ color: '#534AB7' }}>{secilenTalep.mevcutEmail || secilenTalep.mevcutTelefon}</strong>
                </div>
              )}
              {secilenTalep.not && <div style={{ marginTop: 6, fontSize: 12, color: textSec, fontStyle: 'italic' }}>"{secilenTalep.not}"</div>}
            </div>

            {/* Öğrenci arama */}
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Mevcut öğrenciyi seçin</div>
            <input
              autoFocus
              placeholder="Ad, soyad veya e-posta ile ara..."
              value={arama}
              onChange={e => setArama(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${inputBorder}`, background: inputBg, color: isDark ? '#fff' : '#000', fontSize: 13, boxSizing: 'border-box', marginBottom: 10 }}
            />

            {aramaYukleniyor && <div style={{ color: textMuted, fontSize: 13, marginBottom: 10 }}>Aranıyor...</div>}

            {ogrenciler.length > 0 && (
              <div style={{ border: `1px solid ${border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 14 }}>
                {ogrenciler.map((o, i) => (
                  <div key={o.id}
                    onClick={() => setSecilenOgrenci(o)}
                    style={{
                      padding: '10px 14px',
                      cursor: 'pointer',
                      background: secilenOgrenci?.id === o.id ? (isDark ? '#1a1a3a' : '#EEEDFE') : (i % 2 === 0 ? card : cardAlt),
                      borderTop: i > 0 ? `1px solid ${borderSub}` : 'none',
                      borderLeft: secilenOgrenci?.id === o.id ? '3px solid #534AB7' : '3px solid transparent',
                    }}>
                    <div style={{ fontWeight: 500, color: secilenOgrenci?.id === o.id ? '#534AB7' : 'inherit' }}>
                      {o.ad} {o.soyad}
                    </div>
                    <div style={{ fontSize: 11, color: textMuted }}>{o.email}{o.tel ? ` · ${o.tel}` : ''}</div>
                  </div>
                ))}
              </div>
            )}

            {secilenOgrenci && (
              <div style={{ background: isDark ? '#0a1a0a' : '#eafaf1', border: `1px solid ${isDark ? '#1a3a1a' : '#a3e4b8'}`, borderRadius: 8, padding: 12, marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: '#1D9E75', fontWeight: 600, marginBottom: 4 }}>SEÇİLEN HESAP</div>
                <div style={{ fontWeight: 600 }}>{secilenOgrenci.ad} {secilenOgrenci.soyad}</div>
                <div style={{ fontSize: 12, color: textMuted }}>{secilenOgrenci.email}</div>
                <div style={{ fontSize: 12, color: textSec, marginTop: 6 }}>
                  Talep eden kişinin hesap bilgileri bu öğrenciyle eşleştirilecek. Eski hesap kaldırılacak.
                </div>
              </div>
            )}

            {ayniHesap && (
              <div style={{ background: isDark ? '#1a1a00' : '#fffbeb', border: `1px solid ${isDark ? '#333300' : '#fde68a'}`, borderRadius: 6, padding: '8px 12px', fontSize: 12, color: isDark ? '#fde68a' : '#92400e', marginBottom: 12 }}>
                Aynı hesap seçildi. Birleştirme yapılmadan talep kapatılacak.
              </div>
            )}

            {hata && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{hata}</div>}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={modalKapat} style={{ padding: '9px 18px', borderRadius: 8, border: `1px solid ${border}`, background: 'transparent', cursor: 'pointer', fontSize: 13, color: isDark ? '#fff' : '#000' }}>
                İptal
              </button>
              <button
                onClick={birlestir}
                disabled={!secilenOgrenci || islemYapiliyor}
                style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: secilenOgrenci ? (ayniHesap ? '#1D9E75' : '#534AB7') : '#999', color: 'white', cursor: secilenOgrenci ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600 }}>
                {islemYapiliyor ? 'İşleniyor...' : ayniHesap ? '✓ Talebi Kapat' : '⇌ Birleştir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
